/**
 * Player save data.
 *
 * The same structure is written to the server (authoritative) and mirrored into
 * localStorage so single-player works with the network down. It is versioned
 * and `migrateSave` repairs corrupted or outdated payloads instead of throwing.
 */

import { STARTER_CAR_ID, CARS } from './cars.js';
import { TRACKS } from './tracks.js';
import { EMPTY_UPGRADES, UPGRADES, type UpgradeLevels } from './upgrades.js';

export const SAVE_VERSION = 2;

export interface GameSettings {
  masterVolume: number;
  musicVolume: number;
  sfxVolume: number;
  cameraZoom: number;
  screenShake: boolean;
  reducedMotion: boolean;
  pixelScale: number;
  showRacingLine: boolean;
  autoAccelerate: boolean;
  minimap: boolean;
  units: 'metric' | 'imperial';
}

export interface KeyBindings {
  up: string;
  down: string;
  left: string;
  right: string;
  handbrake: string;
  boost: string;
  reset: string;
  camera: string;
}

export interface PlayerStats {
  races: number;
  wins: number;
  podiums: number;
  finishes: number;
  driftScore: number;
  distance: number;
  timePlayed: number;
  cleanLaps: number;
  crashes: number;
}

export interface SaveData {
  version: number;
  money: number;
  xp: number;
  /** Owned car ids. */
  cars: string[];
  selectedCar: string;
  /** carId -> body colour. */
  paint: Record<string, string>;
  /** carId -> upgrade levels. */
  upgrades: Record<string, UpgradeLevels>;
  /** Unlocked track ids (all are unlocked by default; kept for future gating). */
  tracks: string[];
  /** `${trackId}` -> best lap seconds. */
  bestLaps: Record<string, number>;
  /** `${trackId}` -> best race time seconds. */
  bestRaces: Record<string, number>;
  achievements: string[];
  stats: PlayerStats;
  settings: GameSettings;
  controls: KeyBindings;
  /** Last car/track chosen, for quick restart. */
  lastTrack: string;
  lastMode: string;
}

export const DEFAULT_SETTINGS: GameSettings = {
  masterVolume: 0.8,
  musicVolume: 0.45,
  sfxVolume: 0.8,
  cameraZoom: 1,
  screenShake: true,
  reducedMotion: false,
  pixelScale: 3,
  showRacingLine: false,
  autoAccelerate: false,
  minimap: true,
  units: 'metric',
};

export const DEFAULT_CONTROLS: KeyBindings = {
  up: 'KeyW',
  down: 'KeyS',
  left: 'KeyA',
  right: 'KeyD',
  handbrake: 'Space',
  boost: 'ShiftLeft',
  reset: 'KeyR',
  camera: 'KeyC',
};

export const ACHIEVEMENTS: Array<{ id: string; name: string; description: string; reward: number }> = [
  { id: 'first-race', name: 'Green Light', description: 'Finish your first race.', reward: 500 },
  { id: 'first-win', name: 'Top Step', description: 'Win a race.', reward: 1200 },
  { id: 'podium-5', name: 'Regular', description: 'Finish on the podium five times.', reward: 2000 },
  { id: 'clean-lap', name: 'Clean Sheet', description: 'Set a personal best lap on any track.', reward: 700 },
  { id: 'drifter', name: 'Sideways', description: 'Bank 5,000 drift points in one race.', reward: 1500 },
  { id: 'collector', name: 'Collector', description: 'Own four different cars.', reward: 3000 },
  { id: 'tuner', name: 'Tuner', description: 'Install ten upgrade levels on one car.', reward: 2500 },
  { id: 'globe', name: 'Globetrotter', description: 'Race on every track.', reward: 4000 },
  { id: 'ten-races', name: 'Veteran', description: 'Complete ten races.', reward: 2500 },
  { id: 'speed-demon', name: 'Speed Demon', description: 'Reach 480 px/s.', reward: 1800 },
];

export function createDefaultSave(): SaveData {
  return {
    version: SAVE_VERSION,
    money: 6000,
    xp: 0,
    cars: [STARTER_CAR_ID],
    selectedCar: STARTER_CAR_ID,
    paint: { [STARTER_CAR_ID]: CARS[0]!.palettes[0]! },
    upgrades: { [STARTER_CAR_ID]: { ...EMPTY_UPGRADES } },
    tracks: TRACKS.map((t) => t.id),
    bestLaps: {},
    bestRaces: {},
    achievements: [],
    stats: {
      races: 0, wins: 0, podiums: 0, finishes: 0, driftScore: 0,
      distance: 0, timePlayed: 0, cleanLaps: 0, crashes: 0,
    },
    settings: { ...DEFAULT_SETTINGS },
    controls: { ...DEFAULT_CONTROLS },
    lastTrack: TRACKS[0]!.id,
    lastMode: 'quick-race',
  };
}

const num = (v: unknown, fallback: number, min = -Infinity, max = Infinity): number =>
  typeof v === 'number' && Number.isFinite(v) ? Math.min(max, Math.max(min, v)) : fallback;

const bool = (v: unknown, fallback: boolean): boolean => (typeof v === 'boolean' ? v : fallback);

const str = (v: unknown, fallback: string): string => (typeof v === 'string' && v.length < 200 ? v : fallback);

function sanitiseUpgrades(raw: unknown): UpgradeLevels {
  const out: UpgradeLevels = {};
  if (!raw || typeof raw !== 'object') return out;
  for (const def of UPGRADES) {
    const v = (raw as Record<string, unknown>)[def.id];
    const level = Math.floor(num(v, 0, 0, def.levels.length));
    if (level > 0) out[def.id] = level;
  }
  return out;
}

/**
 * Coerces arbitrary (possibly corrupt, possibly older) data into a valid
 * `SaveData`. Unknown fields are dropped, out-of-range values clamped and any
 * missing car/track restored, so a damaged save degrades gracefully instead of
 * bricking the account.
 */
export function migrateSave(raw: unknown): SaveData {
  const base = createDefaultSave();
  if (!raw || typeof raw !== 'object') return base;
  const r = raw as Record<string, unknown>;

  const carIds = new Set(CARS.map((c) => c.id));
  const trackIds = new Set(TRACKS.map((t) => t.id));

  let cars = Array.isArray(r.cars) ? r.cars.filter((c): c is string => typeof c === 'string' && carIds.has(c)) : [];
  if (!cars.includes(STARTER_CAR_ID)) cars.unshift(STARTER_CAR_ID);
  cars = Array.from(new Set(cars));

  const tracks = Array.isArray(r.tracks)
    ? r.tracks.filter((t): t is string => typeof t === 'string' && trackIds.has(t))
    : [];
  for (const t of trackIds) if (!tracks.includes(t)) tracks.push(t);

  const selectedCar = typeof r.selectedCar === 'string' && cars.includes(r.selectedCar) ? r.selectedCar : cars[0]!;

  const paint: Record<string, string> = {};
  if (r.paint && typeof r.paint === 'object') {
    for (const [k, v] of Object.entries(r.paint as Record<string, unknown>)) {
      if (carIds.has(k) && typeof v === 'string' && /^#[0-9a-fA-F]{3,8}$/.test(v)) paint[k] = v;
    }
  }
  for (const c of cars) {
    if (!paint[c]) paint[c] = CARS.find((d) => d.id === c)!.palettes[0]!;
  }

  const upgrades: Record<string, UpgradeLevels> = {};
  if (r.upgrades && typeof r.upgrades === 'object') {
    for (const [k, v] of Object.entries(r.upgrades as Record<string, unknown>)) {
      if (carIds.has(k)) upgrades[k] = sanitiseUpgrades(v);
    }
  }
  for (const c of cars) upgrades[c] ??= {};

  const bestLaps: Record<string, number> = {};
  if (r.bestLaps && typeof r.bestLaps === 'object') {
    for (const [k, v] of Object.entries(r.bestLaps as Record<string, unknown>)) {
      const key = k.split(':')[0] ?? k;
      if (trackIds.has(key)) bestLaps[k] = num(v, 0, 0, 100000);
    }
  }
  const bestRaces: Record<string, number> = {};
  if (r.bestRaces && typeof r.bestRaces === 'object') {
    for (const [k, v] of Object.entries(r.bestRaces as Record<string, unknown>)) {
      if (trackIds.has(k)) bestRaces[k] = num(v, 0, 0, 1000000);
    }
  }

  const statsRaw = (r.stats ?? {}) as Record<string, unknown>;
  const stats: PlayerStats = {
    races: Math.floor(num(statsRaw.races, 0, 0, 1000000)),
    wins: Math.floor(num(statsRaw.wins, 0, 0, 1000000)),
    podiums: Math.floor(num(statsRaw.podiums, 0, 0, 1000000)),
    finishes: Math.floor(num(statsRaw.finishes, 0, 0, 1000000)),
    driftScore: Math.floor(num(statsRaw.driftScore, 0, 0, 1e12)),
    distance: Math.floor(num(statsRaw.distance, 0, 0, 1e12)),
    timePlayed: num(statsRaw.timePlayed, 0, 0, 1e9),
    cleanLaps: Math.floor(num(statsRaw.cleanLaps, 0, 0, 1000000)),
    crashes: Math.floor(num(statsRaw.crashes, 0, 0, 1000000)),
  };

  const sRaw = (r.settings ?? {}) as Record<string, unknown>;
  const settings: GameSettings = {
    masterVolume: num(sRaw.masterVolume, base.settings.masterVolume, 0, 1),
    musicVolume: num(sRaw.musicVolume, base.settings.musicVolume, 0, 1),
    sfxVolume: num(sRaw.sfxVolume, base.settings.sfxVolume, 0, 1),
    cameraZoom: num(sRaw.cameraZoom, base.settings.cameraZoom, 0.5, 2),
    screenShake: bool(sRaw.screenShake, base.settings.screenShake),
    reducedMotion: bool(sRaw.reducedMotion, base.settings.reducedMotion),
    pixelScale: Math.floor(num(sRaw.pixelScale, base.settings.pixelScale, 1, 6)),
    showRacingLine: bool(sRaw.showRacingLine, base.settings.showRacingLine),
    autoAccelerate: bool(sRaw.autoAccelerate, base.settings.autoAccelerate),
    minimap: bool(sRaw.minimap, base.settings.minimap),
    units: sRaw.units === 'imperial' ? 'imperial' : 'metric',
  };

  const cRaw = (r.controls ?? {}) as Record<string, unknown>;
  const controls: KeyBindings = {
    up: str(cRaw.up, base.controls.up),
    down: str(cRaw.down, base.controls.down),
    left: str(cRaw.left, base.controls.left),
    right: str(cRaw.right, base.controls.right),
    handbrake: str(cRaw.handbrake, base.controls.handbrake),
    boost: str(cRaw.boost, base.controls.boost),
    reset: str(cRaw.reset, base.controls.reset),
    camera: str(cRaw.camera, base.controls.camera),
  };

  return {
    version: SAVE_VERSION,
    money: Math.floor(num(r.money, base.money, -1e12, 1e12)),
    xp: Math.floor(num(r.xp, 0, 0, 1e12)),
    cars,
    selectedCar,
    paint,
    upgrades,
    tracks,
    bestLaps,
    bestRaces,
    achievements: Array.isArray(r.achievements)
      ? r.achievements.filter((a): a is string => typeof a === 'string' && ACHIEVEMENTS.some((d) => d.id === a))
      : [],
    stats,
    settings,
    controls,
    lastTrack: typeof r.lastTrack === 'string' && trackIds.has(r.lastTrack) ? r.lastTrack : base.lastTrack,
    lastMode: str(r.lastMode, base.lastMode),
  };
}

/** Driver level from total XP. Each level needs progressively more. */
export function levelFromXp(xp: number): { level: number; intoLevel: number; needed: number } {
  let level = 1;
  let remaining = Math.max(0, xp);
  let need = 1000;
  while (remaining >= need && level < 200) {
    remaining -= need;
    level++;
    need = Math.round(need * 1.18);
  }
  return { level, intoLevel: remaining, needed: need };
}

export function upgradeLevelsFor(save: SaveData, carId: string): UpgradeLevels {
  return save.upgrades[carId] ?? {};
}

/** Reasonable sanity limits for a save payload arriving from a client. */
export const SAVE_LIMITS = {
  maxBytes: 256 * 1024,
  maxMoney: 1e12,
  maxXp: 1e12,
};
